import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import StarRating from '../StarRating/StarRating';
import GrowthIndicator from '../GrowthIndicator/GrowthIndicator';
import { MoreVert } from '@mui/icons-material';
import BenchmarkDataCell from '../BenchmarkDataCell/BenchmarkDataCell';
import Chip from '../Chip/Chip';
import { ICompetitor } from '../../shared/interfaces/ICompetitor';
import ScoreService from '../../services/ScoreService';
import {
  hasRecommends,
  hasReplies,
  IBenchmarkScoreEntryMinimal,
  IScoreEntry,
} from '../../shared/interfaces/IScoreEntry';
import { useTranslation } from 'react-i18next';
import BenchmarkingService from '../../services/BenchmarkingService';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import Confirm from '../Confirm/Confirm';
import { TimeFilters } from '../Benchmark/Benchmark';
import DateUtils from '../../shared/DateUtils';
import i18n from 'i18next';
import WidgetService from '../../services/WidgetService';
import Helper from '../../shared/helpers/Helper';
import PlatformService from '../../services/PlatformService';

interface Props extends ICompetitor {
  platform: string;
  index: number;
  isSubject: boolean;
  sx?: SxProps<Theme>;
  benchmarkID?: string | undefined;
  competitorID?: string | undefined;
  reload: () => Promise<void>;
  scoreDataChange?: (scoreData: IScoreEntry) => void;
  isScoreDetailsExpanded: boolean;
  recommendsTimeFilter: TimeFilters;
  scoreTimeFilter: TimeFilters;
  competition?: string[];
  baseScoreData: IScoreEntry | null;
}

function getObjectKeyByString(obj: { [key: string]: any }, key: string): any {
  if (key.includes('.')) {
    const cur = key.split('.')[0];
    const rest = key.split('.').slice(1).join('.');
    return getObjectKeyByString(obj[cur], rest);
  } else {
    return obj[key];
  }
}

function setObjectKeyByString(obj: { [key: string]: any }, key: string, value: any): any {
  if (key.includes('.')) {
    const cur = key.split('.')[0];
    const rest = key.split('.').slice(1).join('.');
    if (!obj[cur]) {
      obj[cur] = {};
    }
    return setObjectKeyByString(obj[cur], rest, value);
  } else {
    obj[key] = value;
    return;
  }
}

const BenchmarkCompetitor: FC<Props> = ({
  label,
  company,
  url,
  platform,
  index,
  isSubject,
  sx,
  benchmarkID,
  competitorID,
  reload,
  scoreDataChange,
  isScoreDetailsExpanded,
  recommendsTimeFilter,
  scoreTimeFilter,
  competition,
  baseScoreData,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const currentLanguage = i18n.language;

  const [loading, setLoading] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [scoreData, setScoreData] = useState<IScoreEntry | IBenchmarkScoreEntryMinimal | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  if (platform === 'ärzte') {
    platform = 'aerzte';
  }
  if (platform === 'mobile.de') {
    platform = 'mobile';
  }

  function renderValueOrNoEntries(
    value?: string | number | null,
    suffix: string = '',
    fallback: string = t('no_entries')
  ): string {
    if (value === undefined || value === null) {
      return fallback;
    }
    return `${value}${suffix}`;
  }

  const isAverageView = () => index === -1;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget as any);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const deleteCompetitor = async () => {
    if (benchmarkID && competitorID) {
      await ReviewMonitoringService.deleteMonitoring(competitorID);
      await BenchmarkingService.deleteBenchmarkEntry(benchmarkID, competitorID);
      await reload();
      handleClose();
    }
  };

  useEffect(() => {
    if (scoreDataChange && scoreData && 'id' in scoreData) {
      scoreDataChange(scoreData);
    }
  }, [scoreData, scoreDataChange]);

  useEffect(() => {
    const controller = new AbortController();

    const getScoreData = async (platform: string, company: string): Promise<IScoreEntry | null> => {
      const resolvedPlatform = await PlatformService.getPlatformByName(platform);

      if (resolvedPlatform) {
        platform = resolvedPlatform.label;
      }

      const data = await ScoreService.getLastTotalScore(
        platform,
        company,
        setLoading,
        controller.signal
      );

      const scoreSplit = await WidgetService.reviewScoreSplitCompany(
        platform,
        company,
        setLoading,
        controller.signal
      );

      if (!data || !scoreSplit) {
        return null;
      }

      data.scoreBreakdown = scoreSplit.scores;
      data.mostRecentReview = data.mostRecentReview ? new Date(data.mostRecentReview) : undefined;

      return data;
    };

    const getAverageScoreData = async (): Promise<IBenchmarkScoreEntryMinimal | null> => {
      if (!competition) {
        return null;
      }
      const data = [];
      for (const competitor of competition) {
        const comData = await getScoreData(platform, competitor);
        if (comData) data.push(comData);
      }

      const res: Partial<IBenchmarkScoreEntryMinimal> = {};
      const SIMPLE_AGGREGATE_KEYS: (string | [string, number])[] = [
        'score.score',
        ['score.reviewCount', 0],
        'scoreDevelopment.day',
        'scoreDevelopment.month',
        'scoreDevelopment.week',
      ];

      if (hasReplies(data[0])) {
        SIMPLE_AGGREGATE_KEYS.push('avgReplyRate', 'avgReplyTime', 'reviewsWithComment');
      }

      if (hasRecommends(data[0])) {
        SIMPLE_AGGREGATE_KEYS.push(
          'recommendationDevelopment.day',
          'recommendationDevelopment.month',
          'recommendationDevelopment.week',
          'score.recommendation'
        );
      }

      for (const entry of SIMPLE_AGGREGATE_KEYS) {
        const [key, precision] = typeof entry == 'object' ? entry : [entry, 2];
        let sum = 0;
        let count = 0;
        for (const competitorData of data) {
          const value = getObjectKeyByString(competitorData, key);
          if (value === undefined) {
            continue;
          }
          sum += value;
          count += 1;
        }

        let avg;
        if (count === 0) {
          avg = undefined;
        } else {
          avg = sum / count;
          avg = Helper.roundToPrecision(avg, precision);
        }
        setObjectKeyByString(res, key, avg);
      }

      res.scoreBreakdown = [];
      /* Breakdown */
      const sums: { [key: number]: number } = {};
      for (const competitorData of data) {
        for (const score of competitorData.scoreBreakdown) {
          if (!sums[score.score]) {
            sums[score.score] = 0;
          }
          sums[score.score] += score.count;
        }
      }
      for (const score in sums) {
        let avg = sums[score] / data.length;
        avg = Helper.roundToPrecision(avg, 0);
        res.scoreBreakdown.push({ score: parseInt(score), count: avg });
      }

      let mostRecentTime = 0;

      for (const competitorData of data) {
        const reviewDate = competitorData.mostRecentReview;
        if (reviewDate instanceof Date && mostRecentTime < reviewDate.getTime()) {
          mostRecentTime = reviewDate.getTime();
        }
      }

      res.mostRecentReview = mostRecentTime > 0 ? new Date(mostRecentTime) : undefined;

      return res as IBenchmarkScoreEntryMinimal;
    };

    if (isAverageView()) {
      (async () => setScoreData(await getAverageScoreData()))();
    } else {
      (async () => {
        const scoreData = await getScoreData(platform, company);
        setScoreData(scoreData);
      })();
    }

    return () => {
      controller.abort();
    };
  }, [company, platform]);

  const borderProps: SxProps<Theme> = {
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  const getGrowPercentage = (): number => {
    if (!scoreData) return 0;
    switch (scoreTimeFilter) {
      case 'day':
        return scoreData.scoreDevelopment.day;
      case 'month':
        return scoreData.scoreDevelopment.month;
      case 'week':
        return scoreData.scoreDevelopment.week;
    }
  };

  const getRecommendTrend = (): number => {
    if (!scoreData || !hasRecommends(scoreData)) return 0;
    switch (recommendsTimeFilter) {
      case 'day':
        return scoreData.recommendationDevelopment.day;
      case 'month':
        return scoreData.recommendationDevelopment.month;
      case 'week':
        return scoreData.recommendationDevelopment.week;
    }
  };

  const renderReplyTime = (hours?: number): string => {
    const HOURS_IN_DAY = 24;
    const DAYS_IN_WEEK = 7;
    const WEEKS_IN_MONTH = 4;

    if (hours === undefined) {
      return t('no_entries');
    }

    if (hours < HOURS_IN_DAY) {
      return t('lt_1_day');
    }
    if (hours < HOURS_IN_DAY * DAYS_IN_WEEK) {
      return t('lt_7_days');
    }
    if (hours < HOURS_IN_DAY * DAYS_IN_WEEK * 2) {
      return t('lt_14_days');
    }
    if (hours < HOURS_IN_DAY * DAYS_IN_WEEK * WEEKS_IN_MONTH) {
      return t('lt_4_weeks');
    }
    if (hours < HOURS_IN_DAY * DAYS_IN_WEEK * WEEKS_IN_MONTH * 2) {
      return t('lt_2_months');
    }
    if (hours < HOURS_IN_DAY * DAYS_IN_WEEK * WEEKS_IN_MONTH * 6) {
      return t('lt_6_months');
    }

    return t('gt_6_months');
  };

  const renderScoreData = () =>
    scoreData ? (
      <>
        <BenchmarkDataCell sx={borderProps}>
          <Typography variant="body1">{scoreData.score.score}</Typography>
          <StarRating
            value={scoreData.score.score}
            sx={{ ml: theme.spacing(1) }}
          />
        </BenchmarkDataCell>

        <BenchmarkDataCell sx={{ ...borderProps, display: 'flex', flexFlow: 'column' }}>
          <Typography variant="body1">{scoreData.score.reviewCount}</Typography>
          <Box
            sx={{
              maxHeight: isScoreDetailsExpanded ? '280px' : '0px',
              overflow: 'hidden',
              transition: 'max-height 300ms linear',
            }}
          >
            {[1, 2, 3, 4, 5].map((n: number) => (
              <div key={n}>
                {' '}
                <StarRating value={n} />
                <Typography>
                  {scoreData.scoreBreakdown.find((breakdown) => breakdown.score == n)?.count ?? 0}{' '}
                  {t('ratings')}
                </Typography>
              </div>
            ))}
          </Box>
        </BenchmarkDataCell>

        {baseScoreData && hasReplies(baseScoreData) && (
          <>
            <BenchmarkDataCell
              sx={{
                ...borderProps,
                padding: '10px 10px',
                justifyContent: 'center',
                display: 'flex',
                flexFlow: 'column',
              }}
            >
              <Tooltip
                arrow
                title={t('reply_rate_tooltip')}
              >
                <span
                  className={''}
                  style={{
                    width: '100%',
                    backgroundColor: theme.palette.primary.light,
                    height: '30px',
                    borderRadius: '5px',
                    position: 'relative',
                    display: 'block',
                    overflow: 'hidden',
                    marginBottom: '5px',
                  }}
                >
                  <span
                    className={''}
                    style={{
                      width: (hasReplies(scoreData) ? scoreData.replyRate : 0) + '%',
                      backgroundColor: theme.palette.primary.main,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span
                      style={{
                        color: 'white',
                        position: 'absolute',
                        width: '100%',
                        left: 0,
                        textAlign: 'center',
                      }}
                    >
                      {renderValueOrNoEntries(
                        hasReplies(scoreData) ? Math.round(scoreData.avgReplyRate) : null,
                        ' %'
                      )}
                    </span>
                  </span>
                </span>
              </Tooltip>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '0.8rem',
                  color: '#999',
                }}
              >
                <span>0%</span>
                <span>100%</span>
              </div>
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Typography variant="body1">
                {renderReplyTime(hasReplies(scoreData) ? scoreData.avgReplyTime : 0)}
              </Typography>
            </BenchmarkDataCell>
          </>
        )}

        <BenchmarkDataCell sx={borderProps}>
          <Typography variant="body1">
            {scoreData.mostRecentReview?.getTime()
              ? DateUtils.dateMonthYear(scoreData.mostRecentReview, currentLanguage)
              : t('no_entries')}
          </Typography>
        </BenchmarkDataCell>

        {baseScoreData && hasRecommends(baseScoreData) && (
          <>
            <BenchmarkDataCell sx={borderProps}>
              <Typography
                variant="body1"
                sx={{ mr: theme.spacing(1) }}
              >
                {renderValueOrNoEntries(
                  scoreData.score.recommendation === 0 ? undefined : scoreData.score.recommendation,
                  '%'
                )}
              </Typography>
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Typography
                variant="body1"
                sx={{ mr: theme.spacing(1) }}
              >
                {renderValueOrNoEntries(getRecommendTrend(), '%')}
              </Typography>
              <GrowthIndicator growth={getRecommendTrend()} />
            </BenchmarkDataCell>
          </>
        )}
        <BenchmarkDataCell>
          <Typography
            variant="body1"
            sx={{ mr: theme.spacing(1) }}
          >
            {renderValueOrNoEntries(getGrowPercentage(), '%')}
          </Typography>
          <GrowthIndicator growth={getGrowPercentage()} />
        </BenchmarkDataCell>
      </>
    ) : (
      <>
        {' '}
        <CircularProgress
          sx={{ ml: theme.spacing(1) }}
          size={24}
        />
      </>
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
        borderRadius: theme.spacing(2),
        px: theme.spacing(2),
        ...sx,
      }}
    >
      <BenchmarkDataCell
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          alignItems: 'center',
        }}
      >
        {!isAverageView() && (
          <Chip
            label={`${index}`}
            sx={{
              backgroundColor: index === 1 ? theme.palette.primary.main : theme.palette.divider,
              color: index === 1 ? '#f1f1f1' : theme.palette.text.primary,
            }}
          />
        )}{' '}
        <Tooltip
          title={label}
          arrow
        >
          <Link
            href={!isAverageView() ? url : undefined}
            variant="body1"
            underline="none"
            target="_blank"
            sx={{
              fontWeight: isSubject || isAverageView() ? 700 : 400,
            }}
          >
            <>{label.length > 18 ? `${label.slice(0, 15)}...` : label}</>
          </Link>
        </Tooltip>
        {!(isSubject || isAverageView()) && (
          <Box>
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={handleClick}
            >
              <MoreVert />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleClose}
            >
              <MenuItem onClick={() => setDeleteIsOpen(true)}>{t('delete')}</MenuItem>
            </Menu>
            <Confirm
              text={t('delete_confirm')}
              open={deleteIsOpen}
              onCancel={() => {
                setDeleteIsOpen(false);
                handleClose();
              }}
              onOk={async () => await deleteCompetitor()}
            />
          </Box>
        )}
      </BenchmarkDataCell>
      {renderScoreData()}
    </Box>
  );
};

export default BenchmarkCompetitor;
